<script>
   /****************************************************************/
   /* BiPlot                                                       */
   /* --------------------                                         */
   /* shows bi-plot with PCA scores and loadings                   */
   /****************************************************************/

   import { XAxis, YAxis, Box, Segments, Points, TextLabels } from 'svelte-plots-basic/2d';
   import { AXIS_COLOR } from './Defaults.js';
   import { Vector } from 'mdatools/arrays';
   import Axes from './Axes.svelte';

   import { pcares_get, pcamodel_get } from '../wasm/dist/pca.js';


   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
      alpha = 0.05,                  // confidence level for Hotelling ellipse
   } = $props();


   // constant plot parameters
   const pColor = "#2679B2";
   const tColor = "#D22C2F";
   const pColorText = "#667892";
   const tColorText = "#927866";

   // get the parameters needed for the plot
   let model = $derived(props ? props.model : null);
   let res = $derived(props ? props.res : null);
   let resobj = $derived(res ? res[params.resID] : null);

   /**
    * Generates axis title for given component and result object.
    * @param resobj - result object (single)
    * @param ncomp - component index (from 1)
    */
   function getAxisTitle(resobj, ncomp) {
      return `PC${ncomp} (${(resobj.stat.expvar[ncomp - 1] * 100).toFixed(1)}%)`;
   }

   /**
    * Returns plot values for given component 'a'
    * @param a {number} - component number
    * @param resobj {Object} - result object
    * @param model {Object} - model object
    */
   function getValues(a, resobj, model) {

      if (!resobj || !model || a < 1 || a > res.ncomp) return null;

      const t = pcares_get(resobj, 'scores', a).slice();
      const p = pcamodel_get(model, 'loadings', a);

      const nobj = t.length;
      const nvar = p.length;

      let vMin = Number.POSITIVE_INFINITY;
      let vMax = Number.NEGATIVE_INFINITY;

      const tpos = new Array(nobj);
      for (let i = 0; i < nobj; i++ ) {
         t[i] = t[i] / Math.sqrt(eigenvals[a - 1] * (nobj - 1));
         if (vMin > t[i]) vMin = t[i];
         if (vMax < t[i]) vMax = t[i];
         tpos[i] = t[i] < 0 ? 1 : 3;
      }

      const ppos = new Array(nobj);
      for (let i = 0; i < nvar; i++ ) {
         if (vMin > p[i]) vMin = p[i];
         if (vMax < p[i]) vMax = p[i];
         ppos[i] = p[i] < 0 ? 1 : 3;
      }

      const values = new Vector(t);
      const end = new Vector(p);
      const label = getAxisTitle(resobj, a);
      const d = (vMax - vMin) / 15;
      const lim = [vMin - d, vMax + d];
      const mx = Math.max(Math.abs(lim[0]), lim[1]);
      return {values, end, lim, label, tpos, ppos, mx};
   }


   let eigenvals = $derived(model ?  pcamodel_get(model, "eigenvals") : null);
   let xStart = $derived(resobj ? Vector.zeros(resobj.ncols) : null);
   let yStart = $derived(resobj ? Vector.zeros(resobj.ncols) : null);


   let xc = $derived(getValues(params.comp1, resobj, model));
   let yc = $derived(getValues(params.comp2, resobj, model));
   let al = $derived(xc && yc ? Math.max(xc.mx, yc.mx) * 1.05 : 0);

   let limX = $derived(xc ? (params.axesEqual ? [-al, al] : xc.lim) : [0, 1]);
   let limY = $derived(yc ? (params.axesEqual ? [-al, al] : yc.lim) : [0, 1]);
</script>

{#if xc && yc}
<Axes {limX} {limY} xLabel={xc.label} yLabel={yc.label} fileName='plot-biplot' title="Bi-plot (scores and loadings)">

   <!-- axis lines (x = 0 and y = 0) -->
   <Segments xStart={[limX[0]]} xEnd={[limX[1]]} yStart={[0]} yEnd={[0]} lineColor={AXIS_COLOR} />
   <Segments xStart={[0]} xEnd={[0]} yStart={[limY[0]]} yEnd={[limY[1]]} lineColor={AXIS_COLOR} />

   <!-- scores as points -->
   <Points xValues={xc.values} yValues={yc.values} lineColor={tColor} borderWidth={1.5}/>

   <!-- loadings as segments -->
   <Segments {xStart} {yStart} xEnd={xc.end} yEnd={yc.end} lineColor={pColor} />
   <Points xValues={xc.end} yValues={yc.end} marker={1} lineColor={pColor} faceColor={pColor} markerSize={0.4} />

   {#if params.showLabels === 'obj' || params.showLabels === 'both'}
   <TextLabels xValues={xc.values} yValues={yc.values} labels={resobj.objlabels} pos={yc.tpos} textSize={0.75} faceColor={tColorText}/>
   {/if}

   {#if params.showLabels === 'var' || params.showLabels === 'both'}
   <TextLabels xValues={xc.end} yValues={yc.end} labels={resobj.varlabels} textSize={0.75} pos={yc.ppos} faceColor={pColorText}/>
   {/if}
</Axes>
{/if}
